import React, { useState, useEffect } from "react";
import {
  Card,
  Form,
  Button,
  Alert,
  Modal,
  Row,
  Col,
  Spinner,
} from "react-bootstrap";
import {
  AlertTriangle,
  UserMinus,
  CheckCircle,
  ShieldAlert,
  ChevronRight,
  Info,
  Lock,
} from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { Link } from "react-router-dom";
import { RecaptchaVerifier, signInWithPhoneNumber } from "firebase/auth";
import { auth } from "../firebase";

const DeleteAccount = () => {
  const [mobileNumber, setMobileNumber] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);
  const [isDeleted, setIsDeleted] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showOtpScreen, setShowOtpScreen] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [otpValue, setOtpValue] = useState("");

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const validateMobile = (phone) => {
    setOtpValue("");
    const phoneRegex = /^[0-9]{10,15}$/;
    return phoneRegex.test(phone.replace(/\D/g, ""));
  };

  const handleDeleteRequest = async () => {
    if (!validateMobile(mobileNumber)) {
      setErrorMessage("Please enter a valid mobile number.");
      return;
    }

    setIsProcessing(true);
    setErrorMessage("");

    try {
      const validateResponse = await fetch(
        "https://api-dev.track.yeapnigaadi.com/api/auth/validate-account",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            phoneNumber: mobileNumber, // "8276827009" format
          }),
        },
      );

      const validateData = await validateResponse.json();

      if (!validateData.success) {
        setIsProcessing(false);
        setErrorMessage(
          "User not found. This mobile number is not registered.",
        );
        return;
      }
      const formattedPhone = `+91${mobileNumber}`;
      if (window.recaptchaVerifier) {
        window.recaptchaVerifier.clear();
        delete window.recaptchaVerifier;
      }
      window.recaptchaVerifier = new RecaptchaVerifier(
        auth,
        "recaptcha-container",
        {
          size: "invisible",
        },
      );
      const confirmationResult = await signInWithPhoneNumber(
        auth,
        formattedPhone,
        window.recaptchaVerifier,
      );

      window.confirmationResult = confirmationResult;

      setShowOtpScreen(true);
    } catch (error) {
      console.error("OTP Error:", error);

      if (error.code === "auth/too-many-requests") {
        setErrorMessage("Too many attempts. Try again later.");
      } else if (error.code === "auth/invalid-recaptcha-token") {
        setErrorMessage("Security verification expired. Please try again.");
      } else {
        setErrorMessage(error.message);
      }
      if (window.recaptchaVerifier) {
        window.recaptchaVerifier.clear();
        delete window.recaptchaVerifier;
      }
    } finally {
      window.scrollTo(0, 0);
      setIsProcessing(false);
    }
  };

  const handleVerifyOtp = async () => {
    if (otpValue.length !== 6) {
      setErrorMessage("Please enter the 6-digit OTP.");
      return;
    }

    setIsProcessing(true);
    setErrorMessage("");

    try {
      const result = await window.confirmationResult.confirm(otpValue);

      const user = result.user;

      // Get Firebase ID token
      const token = await user.getIdToken();

      // Store token temporarily
      window.userDeleteToken = token;

      setShowConfirmModal(true);
    } catch (error) {
      console.error("OTP Verify Error:", error);
      setErrorMessage("Invalid OTP. Please try again.");
    }

    setIsProcessing(false);
  };

  const confirmDeletion = async () => {
    setShowConfirmModal(false);
    setIsProcessing(true);

    try {
      const response = await fetch(
        "https://api-dev.track.yeapnigaadi.com/api/auth/delete-user",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${window.userDeleteToken}`,
          },
        },
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || "Deletion failed");
      }

      setIsDeleted(true);
    } catch (error) {
      console.error("Deletion error:", error);
      setErrorMessage("Failed to delete account. Please try again.");
    }

    setIsProcessing(false);
  };

  const primaryButtonStyle = {
    backgroundColor: "#101010",
    color: "#dbb128",
    border: "none",
    fontWeight: "700",
    letterSpacing: "0.08em",
    padding: "1rem 2rem",
    borderRadius: "8px",
    transition: "all 0.3s ease",
    boxShadow: "0 4px 14px rgb(0 0 0 / 15%)",
  };

  const cardStyle = {
    borderRadius: "24px",
    border: "none",
    boxShadow: "0 20px 40px rgb(0 0 0 / 8%)",
    overflow: "hidden",
    textAlign: "left",
  };

  if (isDeleted) {
    return (
      <>
        <div className="container">
          <Header />
          <div className="main-area">
            <style>
              {`

            .animate-fade-in {
              animation: fadeIn 0.8s ease-in-out;
            }

            @keyframes fadeIn {
              from { opacity: 0; transform: translateY(10px); }
              to { opacity: 1; transform: translateY(0); }
            }
          `}
            </style>
            <Card
              className="p-5 border-0 animate-fade-in text-center"
              style={{ ...cardStyle, maxWidth: "500px", textAlign: "center" }}
            >
              <div
                className="d-inline-flex align-items-center justify-content-center rounded-circle bg-success bg-opacity-10 text-success mb-4 mx-auto"
                style={{ width: "100px", height: "100px" }}
              >
                <CheckCircle size={48} />
              </div>
              <h2 className="fw-bold mb-3 text-dark">Account Removed</h2>
              <p className="text-secondary lead mb-0">
                Your journey with us has ended. All your data has been securely
                and permanently deleted.
              </p>
            </Card>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <div className="container">
        <Header />
        <div className="main-area">
          <Row
            className="justify-content-center align-items-center"
            style={{ width: "100%" }}
          >
            <Col md={10} lg={7} xl={6}>
              <Card style={cardStyle}>
                <div
                  style={{
                    height: "6px",
                    background: "linear-gradient(90deg, #dbb128, #101010)",
                  }}
                />
                <Card.Body className="">
                  {!showOtpScreen ? (
                    <>
                      <div className="d-flex align-items-center mb-4">
                        <div className="p-2 bg-dark rounded-3 me-3">
                          <ShieldAlert size={24} color="#dbb128" />
                        </div>
                        <h1 className="h2 fw-bold mb-0 text-dark">
                          Delete your account
                        </h1>
                      </div>

                      <Card.Text className="text-secondary mb-4 fs-5">
                        We’re sorry to see you go. Deleting your account will
                        permanently remove your profile and all associated data.
                        This action is <strong>immediate</strong> and cannot be
                        undone.
                      </Card.Text>

                      <Alert
                        variant="light"
                        className="border-0 bg-danger bg-opacity-10 p-4 mb-5 rounded-4 text-start"
                      >
                        <div className="d-flex align-items-center mb-3 text-start">
                          <AlertTriangle
                            className="text-danger me-2"
                            size={24}
                          />
                          <span className="fw-bold text-danger text-uppercase small tracking-wider">
                            Crucial Information
                          </span>
                        </div>
                        <ul className="mb-0 text-dark opacity-75 list-unstyled text-start">
                          <li className="mb-2 d-flex align-items-start">
                            <ChevronRight
                              size={16}
                              className="me-2 mt-1 text-danger flex-shrink-0"
                            />
                            Your profile, settings, and private data will be
                            wiped.
                          </li>
                          <li className="mb-2 d-flex align-items-start">
                            <ChevronRight
                              size={16}
                              className="me-2 mt-1 text-danger flex-shrink-0"
                            />
                            Subscriptions must be cancelled separately.
                          </li>
                          <li className="mb-2 d-flex align-items-start">
                            <ChevronRight
                              size={16}
                              className="me-2 mt-1 text-danger flex-shrink-0"
                            />
                            Public content may remain where required for
                            compliance.
                          </li>
                          <li className="d-flex align-items-start">
                            <ChevronRight
                              size={16}
                              className="me-2 mt-1 text-danger flex-shrink-0"
                            />
                            There is no recovery period once deletion is
                            completed.
                          </li>
                        </ul>
                      </Alert>

                      <div className="bg-light p-4 rounded-4 mb-4 text-start">
                        <h2 className="h6 fw-bold mb-3 text-dark text-uppercase tracking-tight d-flex align-items-center">
                          <Info size={16} className="me-2" />
                          Verify Your Identity
                        </h2>

                        <Form autoComplete="off">
                          <Form.Group
                            className="mb-4"
                            controlId="confirmMobile"
                          >
                            <Form.Label className="small fw-bold text-secondary mb-2 d-block">
                              Confirm Mobile Number
                            </Form.Label>
                            <Form.Control
                              type="tel"
                              placeholder="Enter mobile number"
                              value={mobileNumber}
                              maxLength={10}
                              onChange={(event) => {
                                // Allow only digits
                                const onlyNumbers = event.target.value.replace(
                                  /\D/g,
                                  "",
                                );
                                setMobileNumber(onlyNumbers);
                                setErrorMessage("");
                              }}
                              isInvalid={!!errorMessage}
                              className="py-3 px-4 border-0 shadow-sm rounded-3"
                              style={{ fontSize: "1rem" }}
                            />
                            <Form.Control.Feedback
                              type="invalid"
                              className="mt-2 fw-medium text-start"
                            >
                              {errorMessage}
                            </Form.Control.Feedback>
                          </Form.Group>

                          <Button
                            onClick={handleDeleteRequest}
                            disabled={isProcessing}
                            style={primaryButtonStyle}
                            className="w-100 btn-primary-custom text-uppercase d-flex align-items-center justify-content-center shadow-lg text-center custom-btn"
                          >
                            {isProcessing ? (
                              <>
                                <Spinner
                                  as="span"
                                  animation="border"
                                  size="sm"
                                  role="status"
                                  aria-hidden="true"
                                  className="me-2"
                                />
                                Securely Wiping Data...
                              </>
                            ) : (
                              <>
                                <UserMinus size={20} className="me-2" />
                                Permanently Delete Account
                              </>
                            )}
                          </Button>
                        </Form>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="animate-fade-in">
                        <div className="d-flex align-items-center mb-4">
                          <div className="p-2 bg-dark rounded-3 me-3">
                            <Lock size={24} color="#dbb128" />
                          </div>
                          <h1 className="h2 fw-bold mb-0">Verify Identity</h1>
                        </div>

                        <p className="text-secondary mb-4">
                          We've sent a 6-digit code to{" "}
                          <strong>+91 {mobileNumber}</strong>. Enter it below to
                          confirm deletion.
                        </p>

                        <Form.Group className="mb-4">
                          <Form.Control
                            type="text"
                            placeholder="000000"
                            value={otpValue}
                            maxLength={6}
                            onChange={(event) =>
                              setOtpValue(event.target.value.replace(/\D/g, ""))
                            }
                            className="py-3 px-4 border-0 shadow-sm text-center fw-bold fs-4"
                            style={{ letterSpacing: "0.5em" }}
                            isInvalid={!!errorMessage}
                          />
                          <Form.Control.Feedback
                            type="invalid"
                            className="text-center"
                          >
                            {errorMessage}
                          </Form.Control.Feedback>
                        </Form.Group>

                        <Button
                          onClick={handleVerifyOtp}
                          disabled={isProcessing}
                          style={primaryButtonStyle}
                          className="w-100 mb-3  custom-btn"
                        >
                          {isProcessing ? (
                            <Spinner size="sm" />
                          ) : (
                            "Verify & Delete Permanently"
                          )}
                        </Button>

                        <Button
                          variant="link"
                          className="w-100 text-decoration-none text-muted small"
                          onClick={() => setShowOtpScreen(false)}
                        >
                          Back to Edit Number
                        </Button>
                      </div>
                    </>
                  )}

                  <p className="text-center text-muted small mb-0">
                    Changed your mind?{" "}
                    <Link
                      to="#"
                      className="text-dark fw-bold text-decoration-none border-bottom border-dark border-2"
                    >
                      Keep my account
                    </Link>
                  </p>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </div>
        <Modal
          show={showConfirmModal}
          onHide={() => setShowConfirmModal(false)}
          centered
          backdrop="static"
          contentClassName="border-0 shadow-lg rounded-4 text-start"
        >
          <Modal.Body className="p-5 text-start">
            <div className="mb-4 text-danger text-start">
              <AlertTriangle size={64} strokeWidth={1.5} />
            </div>
            <h3 className="fw-bold text-dark mb-3 text-start">
              Are you absolutely sure?
            </h3>
            <p className="text-secondary mb-4 text-start">
              This action is irreversible. You will lose access to your
              workspace, history, and all stored preferences forever.
            </p>
            <div className="d-grid gap-3">
              <Button
                variant="danger"
                onClick={confirmDeletion}
                className="py-3 fw-bold text-uppercase tracking-wide text-center"
                style={{ borderRadius: "12px" }}
              >
                Confirm Permanent Deletion
              </Button>
              <Button
                variant="link"
                onClick={() => setShowConfirmModal(false)}
                className="text-secondary text-decoration-none fw-bold text-center"
              >
                No, take me back
              </Button>
            </div>
          </Modal.Body>
        </Modal>
      </div>
      <Footer />
      <div id="recaptcha-container"></div>
    </>
  );
};

export default DeleteAccount;
